<script setup lang="ts">
import type { SiteBlockData, SiteBlockSettings } from '../../../types/site'

/**
 * Email capture posting straight to a tenant-configured external endpoint
 * (Formspree, Mailchimp, Getform, …). A native form POST rather than fetch:
 * it needs no JavaScript, has no CORS surface, and is exactly what those
 * providers document.
 *
 * Nothing is stored on this platform. The submission goes to whatever host the
 * tenant configured — the settings form says so explicitly, because the tenant
 * is the data controller for it.
 */
const props = defineProps<{ data: SiteBlockData, settings: SiteBlockSettings }>()

const submitted = ref(false)

const emailName = computed(() => props.data.email_field_name || 'email')
</script>

<template>
  <div
    v-if="data.action_url"
    class="mx-auto max-w-xl text-center"
  >
    <SiteSectionHeading
      :eyebrow="data.eyebrow"
      :title="data.headline"
      :subtitle="data.subtext"
    />

    <form
      :action="data.action_url"
      :method="data.method || 'post'"
      :target="data.target === 'self' ? '_self' : '_blank'"
      rel="noopener"
      accept-charset="UTF-8"
      class="mt-6"
      @submit="submitted = true"
    >
      <input
        v-for="(hidden, i) in data.hidden_fields ?? []"
        :key="i"
        type="hidden"
        :name="hidden.name"
        :value="hidden.value ?? ''"
      >

      <div class="flex flex-col gap-2 sm:flex-row">
        <input
          :name="emailName"
          type="email"
          required
          :placeholder="data.placeholder || 'you@example.com'"
          aria-label="Email address"
          class="w-full rounded-[var(--site-radius)] border border-[var(--site-border)] bg-[var(--site-bg)] px-4 py-2.5 text-[var(--site-fg)]"
        >
        <button
          type="submit"
          class="site-btn-accent shrink-0 px-6 py-2.5 font-semibold transition"
        >
          {{ data.button_text || 'Subscribe' }}
        </button>
      </div>

      <label
        v-if="data.consent_text"
        class="mt-3 flex items-start gap-2 text-left text-sm text-[var(--site-muted)]"
      >
        <input
          type="checkbox"
          name="consent"
          required
          value="yes"
          class="mt-1 shrink-0"
        >
        <span>{{ data.consent_text }}</span>
      </label>
    </form>

    <p
      v-if="submitted && data.success_text"
      class="mt-3 text-sm text-[var(--site-muted)]"
    >
      {{ data.success_text }}
    </p>
  </div>
</template>
